// src/pages/Attendance.js — Creative MUI redesign (no extra libs)
import React, { useState, useEffect, useMemo, useCallback } from 'react';
import {
  Alert,
  Avatar,
  Badge,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Collapse,
  Dialog, DialogTitle, DialogContent, DialogActions,
  Divider,
  FormControlLabel,
  FormGroup,
  IconButton,
  Paper,
  Skeleton,
  Snackbar,
  Stack,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography
} from '@mui/material';
import { useTheme, useMediaQuery } from '@mui/material';
import { apiUrl, OTP_API_URL } from '../config/api';
import AppDateField from '../components/inputs/AppDateField';
import { getSriLankaTodayYmd } from '../lib/sriLankaTime';

// Icons
import VerifiedUserIcon from '@mui/icons-material/VerifiedUser';
import TodayIcon from '@mui/icons-material/Today';
import NavigateBeforeIcon from '@mui/icons-material/NavigateBefore';
import NavigateNextIcon from '@mui/icons-material/NavigateNext';
import RefreshIcon from '@mui/icons-material/Refresh';
import SaveIcon from '@mui/icons-material/Save';
import DoneAllIcon from '@mui/icons-material/DoneAll';
import ClearAllIcon from '@mui/icons-material/ClearAll';
import SearchIcon from '@mui/icons-material/Search';
import FilterAltIcon from '@mui/icons-material/FilterAlt';
import PeopleAltIcon from '@mui/icons-material/PeopleAlt';
import AssignmentTurnedInIcon from '@mui/icons-material/AssignmentTurnedIn';

/* ========================
   API endpoints (unchanged)
   ======================== */
const STAFF_API_URL         = apiUrl('get_staff');
const ATTENDANCE_API_URL    = apiUrl('attendance_management');
const STAFF_OPTIONS_API_URL = apiUrl('attendance_staff_options');
const OTP_API               = OTP_API_URL;

/* ========================
   Helpers
   ======================== */
const fmtDate = (d) => {
  const y = d.getFullYear();
  const m = String(d.getMonth()+1).padStart(2,'0');
  const day = String(d.getDate()).padStart(2,'0');
  return `${y}-${m}-${day}`;
};
const shiftDate = (dateStr, days) => {
  const p = dateStr.split('-').map(Number);
  const d = new Date(p[0], p[1]-1, p[2]);
  d.setDate(d.getDate() + Number(days||0));
  return fmtDate(d);
};
const initials = (name='') => name.trim().split(/\s+/).slice(0,2).map(s=>s[0]?.toUpperCase()||'').join('');
const toArray = (value) => (Array.isArray(value) ? value : []);
const EMPTY_OPTIONS = {
  half_day: false,
  punishment_half_day: false,
  exclude_service_charges: false,
  exclude_tips: false,
};

/* ========================
   OTP dialog (no phone field)
   ======================== */
function OtpDialog({ open, title, details, refId, onVerified, onClose }) {
  const [phase, setPhase] = useState('confirm'); // 'confirm' | 'otp_sent'
  const [otp, setOtp]     = useState('');
  const [busy, setBusy]   = useState(false);
  const [err, setErr]     = useState('');
  const key               = `otp_token_staff_opts_${refId}`;

  useEffect(() => {
    if (open) { setPhase('confirm'); setOtp(''); setBusy(false); setErr(''); }
  }, [open, refId]);

  const sendOtp = async () => {
    setBusy(true); setErr('');
    try {
      const rj = await fetch(OTP_API, {
        method: 'POST', headers: {'Content-Type':'application/json'},
        body: JSON.stringify({ step: 'send', delete_type: 'staff_options', delete_ref: String(refId), details })
      }).then(r=>r.json());
      if (rj.status !== 'ok') throw new Error(rj.message || 'Failed to send OTP');
      localStorage.setItem(key, rj.token);
      setPhase('otp_sent');
    } catch(e){ setErr(e.message); }
    finally{ setBusy(false); }
  };

  const verifyOtp = async () => {
    setBusy(true); setErr('');
    try {
      const token = localStorage.getItem(key);
      if (!token) throw new Error('Missing OTP token. Please resend.');
      const rv = await fetch(OTP_API, {
        method: 'POST', headers: {'Content-Type':'application/json'},
        body: JSON.stringify({ step:'verify', token, otp })
      }).then(r=>r.json());
      if (rv.status !== 'ok') throw new Error(rv.message || 'OTP verification failed');
      await onVerified({ otp, token });
      localStorage.removeItem(key);
      onClose();
    } catch(e){ setErr(e.message); }
    finally{ setBusy(false); }
  };

  return (
    <Dialog open={open} onClose={busy ? undefined : onClose} fullWidth>
      <DialogTitle sx={{ fontWeight:800 }}>{title || 'Confirm Staff Options Update'}</DialogTitle>
      <DialogContent dividers sx={{ display:'flex', flexDirection:'column', gap:2 }}>
        <Typography variant="body2" sx={{ whiteSpace:'pre-wrap' }}>{details}</Typography>
        {phase === 'confirm' && (
          <Alert severity="info" variant="outlined">Click continue to send an OTP to the registered number.</Alert>
        )}
        <Collapse in={phase === 'otp_sent'}>
          <Stack gap={1}>
            <Typography variant="body2">Enter the OTP that was sent:</Typography>
            <TextField
              label="4-digit OTP"
              value={otp}
              onChange={e=>setOtp(e.target.value.replace(/\D+/g,''))}
              inputProps={{ maxLength: 4, inputMode:'numeric' }}
              size="small"
              autoFocus
            />
            {!!err && <Typography color="error">{err}</Typography>}
          </Stack>
        </Collapse>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={busy}>Cancel</Button>
        {phase === 'confirm' ? (
          <Button onClick={sendOtp} disabled={busy} variant="contained" startIcon={<VerifiedUserIcon/>}>
            {busy ? 'Sending…' : 'Send OTP & Continue'}
          </Button>
        ) : (
          <Button onClick={verifyOtp} disabled={busy || otp.length !== 4} variant="contained">
            {busy ? 'Verifying…' : 'Verify & Apply'}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
}

/* ========================
   Main Component
   ======================== */
export default function Attendance(){
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const [attendanceDate, setAttendanceDate] = useState(getSriLankaTodayYmd());
  const [staffList, setStaffList]           = useState([]);

  // Presence map id -> boolean
  const [attendance, setAttendance]         = useState({});
  const [loadingAttendance, setLoadingAttn] = useState(false);

  // Per-staff options
  const [currentOptions, setCurrentOptions] = useState({});
  const [loadedOptions, setLoadedOptions]   = useState({});
  const [optionsLoading, setOptionsLoading] = useState(false);
  const [optionsSaving, setOptionsSaving]   = useState(false);

  // UI extras
  const [query, setQuery] = useState('');
  const [filter, setFilter] = useState('all'); // all | present | changed | withOptions
  const [snack, setSnack] = useState({ open:false, msg:'', severity:'success' });

  // OTP dialog
  const [otpOpen, setOtpOpen] = useState(false);
  const [otpDetails, setOtpDetails] = useState("");

  const showSnack = useCallback((msg, severity='success')=> setSnack({open:true, msg, severity}), []);

  /* ----- Fetch staff ----- */
  const bootStaff = useCallback(async ()=>{
    try {
      setStaffList([]);
      const r = await fetch(STAFF_API_URL).then(r=>r.json());
      const list = Array.isArray(r) ? r : [];
      setStaffList(list);
      const initAtt = {}; const initOpts = {};
      list.forEach(st => {
        initAtt[st.id] = false;
        initOpts[st.id] = { ...EMPTY_OPTIONS };
      });
      setAttendance(initAtt);
      setCurrentOptions(initOpts);
      setLoadedOptions(initOpts);
    } catch (e){ console.error(e); showSnack('Failed to load staff list','error'); }
  }, [showSnack]);

  useEffect(()=>{ bootStaff(); }, [bootStaff]);

  /* ----- Fetch presence for date ----- */
  useEffect(() => {
    if (staffList.length === 0) return;
    setLoadingAttn(true);
    fetch(`${ATTENDANCE_API_URL}?attendance_date=${attendanceDate}`)
      .then(res => res.json())
      .then(data => {
        const updated = {}; staffList.forEach(st => { updated[st.id] = false; });
        const rows = Array.isArray(data) ? data : toArray(data?.items);
        rows.forEach(rec => { updated[rec.staff_id] = rec.attended == 1; });
        setAttendance(updated);
      })
      .catch(err => { console.error(err); showSnack('Error fetching attendance','error'); })
      .finally(() => setLoadingAttn(false));
  }, [attendanceDate, staffList]);

  /* ----- Fetch per-staff options for date ----- */
  const loadStaffOptions = useCallback(() => {
    if (staffList.length === 0) return;
    setOptionsLoading(true);
    fetch(`${STAFF_OPTIONS_API_URL}?date=${attendanceDate}`)
      .then(r => r.json())
      .then(d => {
        if (d.status !== 'ok') throw new Error(d.message || 'Failed to load');
        const snap = {};
        staffList.forEach(st => {
          snap[st.id] = { ...EMPTY_OPTIONS };
        });
        toArray(d.records).forEach(rec => {
          snap[rec.staff_id] = {
            half_day: !!(rec.half_day || rec.punishment_half_day),
            punishment_half_day: !!rec.punishment_half_day,
            exclude_service_charges: !!rec.exclude_service_charges,
            exclude_tips: !!rec.exclude_tips
          };
        });
        setLoadedOptions(snap);
        setCurrentOptions(JSON.parse(JSON.stringify(snap)));
      })
      .catch(err => { console.error(err); showSnack('Failed to load staff options','error'); })
      .finally(() => setOptionsLoading(false));
  }, [attendanceDate, staffList, showSnack]);

  useEffect(() => { loadStaffOptions(); }, [loadStaffOptions]);

  /* ----- Derived: counts, filtered list, dirty state ----- */
  const counts = useMemo(() => {
    const present = Object.values(attendance).filter(Boolean).length;
    let half=0, normalHalf=0, punishHalf=0, nosvc=0, notips=0, any=0;
    Object.keys(currentOptions).forEach(id=>{
      if (!attendance[id]) return;
      const x = currentOptions[id]||EMPTY_OPTIONS;
      const isHalf = !!(x.half_day || x.punishment_half_day);
      const isPunishHalf = !!x.punishment_half_day;
      const isNormalHalf = isHalf && !isPunishHalf;
      const on = isHalf || x.exclude_service_charges || x.exclude_tips;
      if (isHalf) half++;
      if (isNormalHalf) normalHalf++;
      if (isPunishHalf) punishHalf++;
      if (x.exclude_service_charges) nosvc++;
      if (x.exclude_tips) notips++;
      if (on) any++;
    });
    return { total: staffList.length, present, half, normalHalf, punishHalf, nosvc, notips, any };
  }, [attendance, currentOptions, staffList.length]);

  const diff = useMemo(()=>{
    const creates=[], updates=[], deletes=[];
    staffList.forEach(st => {
      const id = st.id;
      const cur = attendance[id]
        ? (currentOptions[id] || EMPTY_OPTIONS)
        : EMPTY_OPTIONS;
      const old = loadedOptions[id] || EMPTY_OPTIONS;
      const curAny = !!(cur.half_day || cur.punishment_half_day || cur.exclude_service_charges || cur.exclude_tips);
      const oldAny = !!(old.half_day || old.punishment_half_day || old.exclude_service_charges || old.exclude_tips);
      if (!oldAny && curAny) { creates.push({ staff_id:id, ...cur }); }
      else if (oldAny && !curAny) { deletes.push(id); }
      else if (oldAny && curAny) {
        if (
          cur.half_day !== old.half_day
          || cur.punishment_half_day !== old.punishment_half_day
          || cur.exclude_service_charges !== old.exclude_service_charges
          || cur.exclude_tips !== old.exclude_tips
        ) {
          updates.push({ staff_id:id, ...cur });
        }
      }
    });
    return { creates, updates, deletes };
  }, [attendance, staffList, currentOptions, loadedOptions]);

  const isDirty = diff.creates.length + diff.updates.length + diff.deletes.length > 0;

  const filteredStaff = useMemo(()=>{
    const q = query.trim().toLowerCase();
    return staffList.filter(st => {
      if (q) {
        const blob = `${st.name||''} ${st.id||''} ${st.phone||''}`.toLowerCase();
        if (!blob.includes(q)) return false;
      }
      if (filter === 'present') return !!attendance[st.id];
      if (filter === 'changed') {
        const cur = attendance[st.id] ? (currentOptions[st.id] || EMPTY_OPTIONS) : EMPTY_OPTIONS;
        const old = loadedOptions[st.id] || EMPTY_OPTIONS;
        return (
          (cur.half_day!==old.half_day)
          || (cur.punishment_half_day!==old.punishment_half_day)
          || (cur.exclude_service_charges!==old.exclude_service_charges)
          || (cur.exclude_tips!==old.exclude_tips)
        );
      }
      if (filter === 'withOptions') {
        if (!attendance[st.id]) return false;
        const c = currentOptions[st.id] || EMPTY_OPTIONS;
        return !!(c.half_day||c.punishment_half_day||c.exclude_service_charges||c.exclude_tips);
      }
      return true;
    });
  }, [staffList, query, filter, attendance, currentOptions, loadedOptions]);

  /* ----- Handlers ----- */
  const handlePresence = (e, staffId) => {
    const checked = !!e.target.checked;
    setAttendance(prev => ({ ...prev, [staffId]: checked }));
    if (!checked) {
      setCurrentOptions(prev => ({
        ...prev,
        [staffId]: { ...EMPTY_OPTIONS },
      }));
    }
  };
  const handleHalfType = (staffId, mode) => (e) => {
    const checked = !!e.target.checked;
    setCurrentOptions(prev => {
      if (!attendance[staffId]) return prev;
      const base = prev[staffId] || EMPTY_OPTIONS;
      if (!checked) {
        return {
          ...prev,
          [staffId]: { ...base, half_day: false, punishment_half_day: false }
        };
      }
      return {
        ...prev,
        [staffId]: {
          ...base,
          half_day: true,
          punishment_half_day: mode === 'punishment',
        }
      };
    });
  };
  const handleOpt = (staffId, key) => (e) => setCurrentOptions(prev => {
    if (!attendance[staffId]) return prev;
    return {
      ...prev,
      [staffId]: { ...(prev[staffId] || EMPTY_OPTIONS), [key]: e.target.checked }
    };
  });

  const submitAttendance = async () => {
    try {
      const payload = { action: "submit_attendance", date: attendanceDate, attendance };
      const rj = await fetch(ATTENDANCE_API_URL, { method:"POST", headers:{"Content-Type":"application/json"}, body: JSON.stringify(payload) }).then(r=>r.json());
      showSnack(rj.message || "Attendance submitted.", 'success');
    } catch(e){ console.error(e); showSnack('Error submitting attendance','error'); }
  };

  const saveStaffOptions = async () => {
    const { creates, updates, deletes } = diff;
    if (creates.length === 0 && updates.length === 0 && deletes.length === 0) {
      showSnack('No changes to save.','info');
      return;
    }
    if (updates.length + deletes.length > 0) {
      const summary = [ `Date: ${attendanceDate}`, `Creates: ${creates.length}`, `Updates: ${updates.length}`, `Deletes: ${deletes.length}` ].join('\n');
      setOtpDetails(summary); setOtpOpen(true); return;
    }
    // Only creates: save without OTP
    setOptionsSaving(true);
    try {
      const r = await fetch(STAFF_OPTIONS_API_URL, { method:'POST', headers:{'Content-Type':'application/json'}, body: JSON.stringify({ action:'save', date: attendanceDate, creates, updates:[], deletes:[] }) }).then(r=>r.json());
      if (r.status !== 'ok') throw new Error(r.message || 'Save failed');
      showSnack(`Saved. Created: ${r.created}, Updated: ${r.updated}, Deleted: ${r.deleted}`,'success');
      loadStaffOptions();
    } catch(e){ showSnack(e.message,'error'); }
    finally{ setOptionsSaving(false); }
  };

  const applyStaffOptionsWithOtp = async ({ otp, token }) => {
    const { creates, updates, deletes } = diff;
    setOptionsSaving(true);
    try {
      const r = await fetch(STAFF_OPTIONS_API_URL, { method:'POST', headers:{ 'Content-Type':'application/json' }, body: JSON.stringify({ action:'save', date: attendanceDate, creates, updates, deletes, otp, otp_token: token }) }).then(r=>r.json());
      if (r.status !== 'ok') throw new Error(r.message || 'Save failed');
      showSnack(`Saved. Created: ${r.created}, Updated: ${r.updated}, Deleted: ${r.deleted}`,'success');
      loadStaffOptions();
    } catch(e){ showSnack(e.message,'error'); }
    finally{ setOptionsSaving(false); }
  };

  // Bulk helpers
  const bulkSetPresence = (val) => setAttendance(prev => {
    const nxt = { ...prev }; filteredStaff.forEach(st => { nxt[st.id] = val; }); return nxt;
  });
  const bulkToggleHalfType = (mode) => setCurrentOptions(prev => {
    const nxt = { ...prev };
    filteredStaff.forEach(st => {
      if (!attendance[st.id]) return;
      const current = nxt[st.id] || EMPTY_OPTIONS;
      const isPunishment = !!current.punishment_half_day;
      const isNormal = !!current.half_day && !isPunishment;
      const isActive = mode === 'punishment' ? isPunishment : isNormal;
      nxt[st.id] = {
        ...current,
        half_day: isActive ? false : true,
        punishment_half_day: isActive ? false : mode === 'punishment',
      };
    });
    return nxt;
  });
  const bulkToggleOpt = (key, onlyPresent=false) => setCurrentOptions(prev => {
    const nxt = { ...prev };
    filteredStaff.forEach(st => {
      if (!attendance[st.id]) return;
      if (onlyPresent && !attendance[st.id]) return;
      nxt[st.id] = { ...(nxt[st.id] || EMPTY_OPTIONS), [key]: !nxt[st.id]?.[key] };
    });
    return nxt;
  });

  const headerGradient = {
    background: `linear-gradient(135deg, ${theme.palette.primary.light}22, ${theme.palette.primary.main}11)`,
    border: `1px solid ${theme.palette.divider}`,
  };

  return (
    <Box sx={{ p: { xs:1.5, sm:2 } }}>
      {/* ===== Header / Date Controls ===== */}
      <Paper elevation={0} sx={{ p:2, mb:2, borderRadius:3, ...headerGradient }}>
        <Stack direction={{ xs:'column', sm:'row' }} alignItems={{ xs:'stretch', sm:'center' }} justifyContent="space-between" gap={1.5}>
          <Stack direction="row" alignItems="center" gap={1.25}>
            <PeopleAltIcon color="primary"/>
            <Typography variant="h5" fontWeight={800}>Attendance</Typography>
            {isDirty && <Chip color="warning" label="Unsaved changes" size="small"/>}
          </Stack>
          <Stack direction={{ xs:'column', sm:'row' }} alignItems={{ xs:'stretch', sm:'center' }} gap={1}>
            <Stack direction="row" gap={0.5}>
              <Tooltip title="Previous day"><span><IconButton onClick={()=>setAttendanceDate(d=>shiftDate(d,-1))}><NavigateBeforeIcon/></IconButton></span></Tooltip>
              <Tooltip title="Today"><span><IconButton onClick={()=>setAttendanceDate(getSriLankaTodayYmd())}><TodayIcon/></IconButton></span></Tooltip>
              <Tooltip title="Next day"><span><IconButton onClick={()=>setAttendanceDate(d=>shiftDate(d,1))}><NavigateNextIcon/></IconButton></span></Tooltip>
            </Stack>
            <AppDateField size="small" label="Date" value={attendanceDate} onChange={e=>setAttendanceDate(e.target.value)} helperText="DD/MM/YYYY" />
            <Tooltip title="Reload day data"><span><IconButton onClick={()=>{ /* re-pull both */ setAttendanceDate(d=>d); loadStaffOptions(); }}><RefreshIcon/></IconButton></span></Tooltip>
          </Stack>
        </Stack>

        <Divider sx={{ my:1.5 }} />

        {/* Quick stats */}
        <Stack direction={{ xs:'column', sm:'row' }} gap={1} alignItems={{ xs:'stretch', sm:'center' }}>
          <Chip icon={<PeopleAltIcon/>} label={`Total: ${counts.total}`} size="small"/>
          <Chip color="success" icon={<AssignmentTurnedInIcon/>} label={`Present: ${counts.present}`} size="small"/>
          <Chip variant="outlined" label={`Half Day: ${counts.half}`} size="small"/>
          <Chip variant="outlined" label={`Normal Half: ${counts.normalHalf}`} size="small"/>
          <Chip color="warning" variant="outlined" label={`Punish Half: ${counts.punishHalf}`} size="small"/>
          <Chip variant="outlined" label={`No Service: ${counts.nosvc}`} size="small"/>
          <Chip variant="outlined" label={`No Tips: ${counts.notips}`} size="small"/>
        </Stack>
      </Paper>

      {/* ===== Toolbar: search, filters, bulk actions ===== */}
      <Paper elevation={0} sx={{ p:1.5, mb:2, borderRadius:3, border:`1px solid ${theme.palette.divider}` }}>
        <Stack direction={{ xs:'column', md:'row' }} gap={1} alignItems={{ xs:'stretch', md:'center' }} justifyContent="space-between">
          <Stack direction={{ xs:'column', md:'row' }} gap={1} alignItems={{ xs:'stretch', md:'center' }}>
            <Box sx={{ position:'relative', minWidth: { xs:'100%', md: 360 } }}>
              <TextField fullWidth size="small" placeholder="Search name / phone" value={query} onChange={e=>setQuery(e.target.value)} InputProps={{ startAdornment: <SearchIcon sx={{ mr:1, opacity:.6 }} /> }} />
            </Box>
            <Stack direction="row" gap={1} alignItems="center">
              <FilterAltIcon sx={{ opacity:.6 }}/>
              <ToggleButtonGroup size="small" exclusive value={filter} onChange={(e,v)=>{ if(v) setFilter(v); }}>
                <ToggleButton value="all">All</ToggleButton>
                <ToggleButton value="present">Present</ToggleButton>
                <ToggleButton value="withOptions">With Options</ToggleButton>
                <ToggleButton value="changed">Changed</ToggleButton>
              </ToggleButtonGroup>
            </Stack>
          </Stack>

          <Stack direction={{ xs:'column', md:'row' }} gap={1}>
            <Tooltip title="Mark all (filtered) present"><span><Button size="small" variant="outlined" startIcon={<DoneAllIcon/>} onClick={()=>bulkSetPresence(true)}>All Present</Button></span></Tooltip>
            <Tooltip title="Clear all (filtered) presence"><span><Button size="small" variant="outlined" startIcon={<ClearAllIcon/>} onClick={()=>bulkSetPresence(false)}>Clear</Button></span></Tooltip>
            <Tooltip title="Toggle normal half day on filtered list"><span><Button size="small" onClick={()=>bulkToggleHalfType('normal')}>Normal Half ±</Button></span></Tooltip>
            <Tooltip title="Toggle punishment half day on filtered list"><span><Button size="small" color="warning" onClick={()=>bulkToggleHalfType('punishment')}>Punish Half ±</Button></span></Tooltip>
            <Tooltip title="Toggle No Service on filtered list"><span><Button size="small" onClick={()=>bulkToggleOpt('exclude_service_charges')}>No Service ±</Button></span></Tooltip>
            <Tooltip title="Toggle No Tips on filtered list"><span><Button size="small" onClick={()=>bulkToggleOpt('exclude_tips')}>No Tips ±</Button></span></Tooltip>
          </Stack>
        </Stack>
      </Paper>

      {/* ===== Staff list ===== */}
      {(loadingAttendance || optionsLoading) ? (
        <Box sx={{ display:'grid', gridTemplateColumns: { xs:'1fr', md:'1fr 1fr' }, gap:1.5 }}>
          {Array.from({length:8}).map((_,i)=> (
            <Paper key={i} sx={{ p:1.5, borderRadius:2 }}>
              <Stack direction="row" gap={1.5} alignItems="center">
                <Skeleton variant="circular" width={40} height={40}/>
                <Box sx={{ flex:1 }}>
                  <Skeleton width="40%" height={20}/>
                  <Skeleton width="60%" height={16}/>
                </Box>
              </Stack>
              <Skeleton sx={{ mt:1 }} height={28}/>
            </Paper>
          ))}
        </Box>
      ) : (
        <Box sx={{ display:'grid', gridTemplateColumns: { xs:'1fr', md:'1fr 1fr' }, gap:1.5 }}>
          {filteredStaff.map((st, index) => {
            const present = !!attendance[st.id];
            const opts = present ? (currentOptions[st.id] || EMPTY_OPTIONS) : EMPTY_OPTIONS;
            const changed = (() => {
              const old = loadedOptions[st.id] || EMPTY_OPTIONS;
              return (
                (opts.half_day!==old.half_day)
                || (opts.punishment_half_day!==old.punishment_half_day)
                || (opts.exclude_service_charges!==old.exclude_service_charges)
                || (opts.exclude_tips!==old.exclude_tips)
              );
            })();

            return (
              <Paper key={st.id} sx={{ p:1.5, borderRadius:2, border:`1px solid ${theme.palette.divider}`,
                backgroundColor: present ? (theme.palette.success.light + '11') : 'transparent' }}>
                <Stack direction="row" alignItems={{ xs:'flex-start', sm:'center' }} gap={1.5}>
                  <Badge color={present? 'success':'default'} variant={present? 'dot':'standard'} overlap="circular">
                    <Avatar sx={{ width:40, height:40 }}>{initials(st.name)}</Avatar>
                  </Badge>
                  <Box sx={{ flex:1, minWidth:0 }}>
                    <Stack direction="row" alignItems="center" gap={1} flexWrap="wrap">
                      <Chip size="small" variant="outlined" label={index + 1} />
                      <Typography variant="subtitle1" fontWeight={700} noWrap>{st.name}</Typography>
                      {st.phone ? <Chip size="small" variant="outlined" label={st.phone}/> : null}
                      {changed && <Chip size="small" color="warning" label="changed"/>}
                    </Stack>
                    <Stack direction={isMobile?'column':'row'} alignItems={isMobile?'flex-start':'center'} justifyContent="space-between" gap={1} sx={{ mt:.5 }}>
                      <FormControlLabel control={<Checkbox checked={present} onChange={(e)=>handlePresence(e, st.id)} />} label="Present" />
                      <FormGroup row>
                        <FormControlLabel control={<Checkbox checked={!!opts.half_day && !opts.punishment_half_day} onChange={handleHalfType(st.id,'normal')} disabled={!present} />} label="Normal Half" />
                        <FormControlLabel control={<Checkbox checked={!!opts.punishment_half_day} onChange={handleHalfType(st.id,'punishment')} disabled={!present} />} label="Punish Half" />
                        <FormControlLabel control={<Checkbox checked={opts.exclude_service_charges} onChange={handleOpt(st.id,'exclude_service_charges')} disabled={!present} />} label="No Service" />
                        <FormControlLabel control={<Checkbox checked={opts.exclude_tips} onChange={handleOpt(st.id,'exclude_tips')} disabled={!present} />} label="No Tips" />
                      </FormGroup>
                    </Stack>
                  </Box>
                </Stack>
              </Paper>
            );
          })}
          {filteredStaff.length === 0 && (
            <Paper sx={{ p:3, borderRadius:2, textAlign:'center' }}>
              <Typography variant="body1">No staff found with the current filters.</Typography>
            </Paper>
          )}
        </Box>
      )}

      {/* ===== Actions ===== */}
      <Box sx={{ height:72 }} />
      <Paper elevation={3} sx={{ position:'fixed', left:16, right:16, bottom:16, borderRadius:3, p:1.25, backdropFilter:'blur(6px)', border:`1px solid ${theme.palette.divider}`,
        backgroundColor: theme.palette.mode==='light' ? '#ffffffee' : '#0b0f14ee' }}>
        <Stack direction={{ xs:'column', md:'row' }} gap={1} alignItems={{ xs:'stretch', md:'center' }} justifyContent="space-between">
          <Stack direction="row" gap={1} alignItems="center" flexWrap="wrap">
            <Chip icon={<PeopleAltIcon/>} label={`${counts.present}/${counts.total} present`} size="small" />
            <Chip variant="outlined" label={`with options: ${counts.any}`} size="small" />
            {isDirty && <Chip color="warning" label={`diff +${diff.creates.length} / ~${diff.updates.length} / -${diff.deletes.length}`} size="small" />}
          </Stack>
          <Stack direction={{ xs:'column', md:'row' }} gap={1}>
            <Button size="large" variant="outlined" startIcon={<AssignmentTurnedInIcon/>} onClick={submitAttendance}>Submit Attendance</Button>
            <Button size="large" variant="contained" startIcon={<SaveIcon/>} disabled={optionsLoading || optionsSaving} onClick={saveStaffOptions}>
              {optionsSaving ? 'Saving…' : 'Save Staff Options'}
            </Button>
          </Stack>
        </Stack>
      </Paper>

      {/* Snackbar */}
      <Snackbar open={snack.open} autoHideDuration={3000} onClose={()=>setSnack(s=>({...s,open:false}))} anchorOrigin={{ vertical:'bottom', horizontal:'right' }}>
        <Alert onClose={()=>setSnack(s=>({...s,open:false}))} severity={snack.severity} variant="filled">{snack.msg}</Alert>
      </Snackbar>

      {/* OTP dialog */}
      <OtpDialog
        open={otpOpen}
        title="Confirm Staff Options Changes"
        details={otpDetails}
        refId={attendanceDate}
        onVerified={applyStaffOptionsWithOtp}
        onClose={()=>setOtpOpen(false)}
      />
    </Box>
  );
}
